import React, { useState, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  Box,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { TbRepeat } from 'react-icons/tb';
import { useRegions, Region } from '../context/RegionsContext';
import { useAudioContext } from '../context/AudioContext';

export const REGION_COLORS = [
  { name: 'white', value: '#FFFFFF' },
  { name: 'red', value: '#EF4444' },
  { name: 'orange', value: '#F97316' },
  { name: 'yellow', value: '#EAB308' },
  { name: 'green', value: '#22C55E' },
  { name: 'blue', value: '#3B82F6' },
  { name: 'purple', value: '#A855F7' },
] as const;

interface RegionSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileId: string;
  currentTime: number;
  hasOpenRegion: boolean;
  editingRegion?: Region | null; // If provided, we're editing an existing region
}

const RegionSetupModal: React.FC<RegionSetupModalProps> = ({
  isOpen,
  onClose,
  fileId,
  currentTime,
  hasOpenRegion,
  editingRegion = null,
}) => {
  const { addRegion, updateRegion, endOpenRegion, removeRegion, getRegions } = useRegions();
  const { audioElements, setLoopStart, setLoopEnd } = useAudioContext();
  
  // Get the next color in sequence for new regions
  const getNextColor = useCallback(() => {
    if (editingRegion) {
      return editingRegion.color;
    }
    
    const regions = getRegions(fileId);
    if (regions.length === 0) {
      return REGION_COLORS[0].value;
    }
    
    // Get the last region's color
    const lastRegion = regions[regions.length - 1];
    const lastColorIndex = REGION_COLORS.findIndex(c => c.value === lastRegion.color);
    
    // If found, use next color (wrap around)
    if (lastColorIndex !== -1) {
      const nextIndex = (lastColorIndex + 1) % REGION_COLORS.length;
      return REGION_COLORS[nextIndex].value;
    }
    
    // Fallback to first color
    return REGION_COLORS[0].value;
  }, [editingRegion, fileId, getRegions]);
  
  const [regionName, setRegionName] = useState(editingRegion?.name || '');
  const [selectedColor, setSelectedColor] = useState<string>(getNextColor());
  const [action, setAction] = useState<'end' | 'add' | null>(null);
  
  // Update state when editingRegion or isOpen changes
  React.useEffect(() => {
    if (editingRegion) {
      setRegionName(editingRegion.name);
      setSelectedColor(editingRegion.color);
    } else {
      setRegionName('');
      setSelectedColor(getNextColor());
    }
  }, [editingRegion, getNextColor, isOpen]);

  const handleClose = useCallback(() => {
    setRegionName('');
    setSelectedColor(REGION_COLORS[0].value);
    setAction(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    if (!regionName.trim()) {
      return;
    }

    if (hasOpenRegion && action === null) {
      // User needs to choose an action first
      return;
    }

    if (hasOpenRegion && action === 'end') {
      // End the previous open region
      endOpenRegion(fileId, currentTime);
      handleClose();
      return;
    }

    if (editingRegion) {
      // Update existing region
      updateRegion(fileId, editingRegion.startTime, {
        name: regionName.trim().substring(0, 30),
        color: selectedColor,
      });
      handleClose();
      return;
    }

    // Add new region (either no open region, or user chose to add new)
    const newRegion: Region = {
      name: regionName.trim().substring(0, 30),
      color: selectedColor,
      startTime: currentTime,
      endTime: null, // Open region
    };

    addRegion(fileId, newRegion);
    handleClose();
  }, [regionName, selectedColor, currentTime, fileId, hasOpenRegion, action, editingRegion, addRegion, updateRegion, endOpenRegion, handleClose]);

  const handleRemove = useCallback(() => {
    if (editingRegion) {
      removeRegion(fileId, editingRegion.startTime);
      handleClose();
    }
  }, [editingRegion, fileId, removeRegion, handleClose]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && regionName.trim()) {
      if (hasOpenRegion && action === null) {
        return; // Can't submit without choosing action
      }
      handleSubmit();
    }
  }, [regionName, hasOpenRegion, action, handleSubmit]);

  const handleSetLoop = useCallback(() => {
    if (!editingRegion) return;

    // Find the trackNumber by looking for the audio player element with matching fileId
    const audioPlayerElement = document.querySelector(`[data-file-id="${fileId}"]`);
    
    if (!audioPlayerElement) {
      console.warn('Could not find audio player element for fileId:', fileId);
      return;
    }

    const playerNumberAttr = audioPlayerElement.getAttribute('data-player-number');
    if (!playerNumberAttr) {
      console.warn('Could not find data-player-number attribute');
      return;
    }

    const trackNumber = parseInt(playerNumberAttr, 10);
    if (isNaN(trackNumber)) {
      console.warn('Invalid trackNumber:', playerNumberAttr);
      return;
    }

    const audio = audioElements.get(trackNumber);
    if (!audio) {
      console.warn('Could not find audio element for trackNumber:', trackNumber);
      return;
    }

    const startTime = editingRegion.startTime;
    const endTime = editingRegion.endTime ?? audio.duration;

    if (endTime > startTime) {
      setLoopStart(startTime);
      setLoopEnd(endTime);
    }

    // Dismiss the modal after setting loop points
    handleClose();
  }, [editingRegion, fileId, audioElements, setLoopStart, setLoopEnd, handleClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent maxW="md" mx={4}>
        <ModalHeader position="relative">
          {editingRegion ? 'Edit Region' : 'Create Region'}
          {editingRegion && (
            <Tooltip label="Set loop points to region boundaries" placement="left">
              <IconButton
                aria-label="Set loop to region"
                icon={<TbRepeat size={36} />}
                size="md"
                variant="ghost"
                position="absolute"
                right="8px"
                top="50%"
                transform="translateY(-50%)"
                onClick={handleSetLoop}
              />
            </Tooltip>
          )}
        </ModalHeader>
        <ModalBody pb={4}>
          <VStack spacing={4} align="stretch">
            {hasOpenRegion && action === null && (
              <VStack spacing={4} align="stretch">
                <Text color="gray.300" fontSize="sm" textAlign="center">
                  An open region exists. What would you like to do?
                </Text>
                <HStack spacing={4} justify="center">
                  <Button
                    onClick={() => setAction('end')}
                    colorScheme="blue"
                    variant="outline"
                    size="md"
                    minW="140px"
                  >
                    End Previous Region
                  </Button>
                  <Button
                    onClick={() => setAction('add')}
                    colorScheme="blue"
                    variant="outline"
                    size="md"
                    minW="140px"
                  >
                    Add New Region
                  </Button>
                </HStack>
              </VStack>
            )}

            {(action !== null || !hasOpenRegion) && (
              <>
                <FormControl isRequired>
                  <Input
                    value={regionName}
                    onChange={(e) => setRegionName(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Region name"
                    maxLength={30}
                    autoFocus
                    bg="whiteAlpha.50"
                    borderColor="whiteAlpha.200"
                    _hover={{ borderColor: "whiteAlpha.300" }}
                    _focus={{ borderColor: "accent.500" }}
                  />
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    {regionName.length}/30 characters
                  </Text>
                </FormControl>

                <FormControl>
                  <HStack spacing={3} justify="center">
                    {REGION_COLORS.map((color) => (
                      <Box
                        key={color.name}
                        p="2px"
                        borderRadius="md"
                        border="2px solid"
                        borderColor={selectedColor === color.value ? "accent.400" : "transparent"}
                        cursor="pointer"
                        onClick={() => setSelectedColor(color.value)}
                        _hover={{
                          borderColor: selectedColor === color.value ? "accent.400" : "whiteAlpha.400",
                        }}
                        transition="all 0.2s"
                      >
                        <Box
                          w="40px"
                          h="40px"
                          borderRadius="md"
                          bg={color.value}
                        />
                      </Box>
                    ))}
                  </HStack>
                </FormControl>
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          {editingRegion && (
            <Button
              variant="ghost"
              colorScheme="red"
              onClick={handleRemove}
              mr="auto"
            >
              Remove Region
            </Button>
          )}
          <Button variant="ghost" onClick={handleClose} mr={2}>
            Cancel
          </Button>
          {(action !== null || !hasOpenRegion || editingRegion) && (
            <Button
              onClick={handleSubmit}
              colorScheme="blue"
              isDisabled={!regionName.trim()}
            >
              {editingRegion ? 'Save Changes' : 'Create Region'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RegionSetupModal;

